import React, { useState, useEffect } from 'react';
import { Search, TrendingUp, Users, DollarSign, Activity } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from 'recharts';
import styles from './NebulaSearch.module.sass';
import { ConstructionBadge, ConstructionOverlay } from 'utils/ConstructionBadge';

const NebulaSearch = () => {
  const [query, setQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  // Load contract address from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const contractParam = urlParams.get('contract');
    if (contractParam) {
      setQuery(contractParam);
    }
  }, []);

  // Load contract address from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const contractParam = urlParams.get('contract');
    if (contractParam) {
      setQuery(contractParam);
    }
  }, []);

  const exampleQueries = [
    "Who's currently interacting with Uniswap V3?",
    "Show me large whale movements in the last hour",
    "What's happening with NFT trading volume?",
    "Analyze recent governance proposals"
  ];

  const handleSearch = () => {
    // Simply set search results to trigger the results section display
    setSearchResults(true);
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      const timestamp = data?.timestamp;
      const formattedDate = timestamp ? new Date(timestamp).toLocaleString() : label;
      
      return (
        <div style={{ 
          backgroundColor: 'rgba(23, 23, 30, 0.95)', 
          borderColor: 'rgba(115, 246, 255, 0.2)',
          color: '#73F6FF',
          padding: '10px',
          borderRadius: '4px',
          fontSize: '12px',
          border: '1px solid rgba(115, 246, 255, 0.2)',
          minWidth: '200px'
        }}>
          <p style={{ color: '#73F6FF', fontWeight: 600, marginBottom: '5px' }}>
            {formattedDate}
          </p>
          
          {payload.map((entry, index) => (
            <p key={index} style={{ color: '#73F6FF', margin: '5px 0' }}>
              {entry.dataKey === 'volume' ? 'Volume' : 'Users'}: <strong>
                {entry.dataKey === 'volume' 
                  ? `$${(entry.value / 1000000).toFixed(1)}M`
                  : entry.value.toLocaleString()
                }
              </strong>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom Y-axis tick
  const CustomYAxisTick = ({ x, y, payload }) => {
    const formattedValue = new Intl.NumberFormat('en-US', { 
      notation: 'compact', 
      maximumFractionDigits: 1 
    }).format(payload.value);
    
    return (
      <text 
        x={x} 
        y={y} 
        dy={4} 
        fill="#73F6FF40" 
        fontSize={12}
        textAnchor="end"
      >
        {formattedValue}
      </text>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.searchSection}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div className={styles.title} style={{ marginBottom: '0px' }}>Nebula</div>
            <div className={styles.subtitle}>AI-Powered Blockchain Analytics</div>
          </div>
          
          <div className={`${styles.searchContainer} ${isSearchFocused ? styles.focused : ''}`}>
            <div className={styles.searchBackdrop} />
            <div className={styles.queryInput}>
              <input
                type="text"
                className={styles.searchInput}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder="Ask anything about on-chain activity..."
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button className={styles.searchButton} onClick={handleSearch}>
                <Search size={20} />
              </button>
            </div>
          </div>

          <div className={styles.searchHints}>
            <div className={styles.hintLabel}>Try asking about</div>
            <div className={styles.hintTags}>
              {exampleQueries.map((query, index) => (
                <div 
                  key={index} 
                  className={styles.hintTag}
                  onClick={() => setQuery(query)}
                >
                  {query}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.resultsContainer} style={{ position: 'relative' }}>
          <div className={styles.resultsHeader}>
            <span>Results</span>
            <div className={styles.resultsDivider} />
          </div>
          
          <div className={styles.visualizationGrid}>
            {searchResults ? (
              <>
                <div className={styles.gridItem}>Chart 1</div>
                <div className={styles.gridItem}>Chart 2</div>
              </>
            ) : (
              <>
                <div className={styles.gridItem}>Chart 1</div>
                <div className={styles.gridItem}>Chart 2</div>
              </>
            )}
          </div>

          <div className={styles.dataContainer}>
            {searchResults ? (
              <div className={styles.placeholder}>
                Results will appear here
              </div>
            ) : (
              <div className={styles.placeholder}>
                Enter a query to see blockchain analytics
              </div>
            )}
          </div>
          
          <ConstructionOverlay />
        </div>
      </div>
    </div>
  );
};

export default NebulaSearch;